// email-signup.js
// Requires Bootstrap 5's JS to be available (either imported or on window.bootstrap)

function getBootstrapToastCtor() {
    // If you import bootstrap in your bundle, it will be available directly.
    // If bootstrap is globally available, it's on window.bootstrap.
    const bs = (typeof bootstrap !== "undefined") ? bootstrap : window.bootstrap;
    return bs?.Toast ?? null;
}

function showToast(rootEl, message, { title = "Email signup", isError = false } = {}) {
    const toastEl = rootEl.querySelector(".port-email-toast");
    const titleEl = rootEl.querySelector(".port-email-toast-title");
    const bodyEl = rootEl.querySelector(".port-email-toast-body");

    if (!toastEl || !bodyEl) return;

    if (titleEl) titleEl.textContent = title;
    bodyEl.textContent = message;

    // Optional: style error vs success via classes
    toastEl.classList.toggle("text-bg-danger", isError);
    toastEl.classList.toggle("text-bg-success", !isError);

    const ToastCtor = getBootstrapToastCtor();
    if (!ToastCtor) {
        // Fallback if toast isn't available: basic alert
        console.warn("Bootstrap.Toast not found; falling back to alert()");
        alert(message);
        return;
    }

    const toast = ToastCtor.getOrCreateInstance(toastEl, { autohide: true, delay: 4000 });
    toast.show();
}

function parseListIds(rootEl) {
    // Prefer data-email-lists as JSON (recommended)
    const raw = rootEl.getAttribute("data-email-lists");
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

async function postEmailSignup({ email, listIds }) {
    // If your endpoint expects form-urlencoded (like old jQuery $.ajax),
    // use URLSearchParams; otherwise switch to JSON.
    const body = new URLSearchParams();
    body.set("email", email);

    // listIds can be posted as repeated keys to match MVC binder: listIds=1&listIds=2
    for (const id of (listIds || [])) body.append("listIds", String(id));

    const res = await fetch("/EmailSubscription/EmailSignup", {
        method: "POST",
        headers: {
            "Accept": "application/json, text/plain, */*"
            // NOTE: fetch will set Content-Type for URLSearchParams automatically in most browsers
        },
        body
    });

    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Request failed (${res.status})`);
    }

    // If your endpoint returns JSON, you can: return await res.json();
    return await res.text().catch(() => "");
}

/**
 * Initialize ONE widget root element.
 */
export function initEmailSignupWidget(rootEl) {
    if (!rootEl) return;

    // Prevent double-init
    if (rootEl.dataset.emailSignupInitialized === "true") return;
    rootEl.dataset.emailSignupInitialized = "true";

    const button = rootEl.querySelector(".btn-email-signup");
    const emailInput = rootEl.querySelector(".email-control");
    const intro = rootEl.querySelector(".signup-intro");
    const inputGroup = rootEl.querySelector(".input-group");

    if (!button || !emailInput) return;

    const listIds = parseListIds(rootEl);

    button.addEventListener("click", async (e) => {
        e.preventDefault();

        const email = (emailInput.value || "").trim();

        if (!email) {
            if (intro) intro.innerHTML = "<span class='error-message'>Email is required</span>";
            showToast(rootEl, "Please enter an email address.", { isError: true });
            return;
        }

        // Basic UX: disable while saving
        const originalText = button.textContent;
        button.disabled = true;
        button.textContent = "Saving...";

        try {
            await postEmailSignup({ email, listIds });

            // Success UI
            if (inputGroup) inputGroup.style.display = "none";
            if (intro) {
                intro.style.textAlign = "center";
                intro.textContent = "Thank you for signing up for our email notifications! You should receive a confirmation email shortly.";
            }

            showToast(rootEl, "Successfully added to the list!", { isError: false });
        } catch (err) {
            console.error("Error posting content.", err);
            if (intro) intro.innerHTML = "<span class='error-message'>Error adding your email.</span>";
            showToast(rootEl, "Error adding your email to this list.", { isError: true });
        } finally {
            button.disabled = false;
            button.textContent = originalText;
        }
    });
}

/**
 * Initialize ALL widgets on the page.
 */
export function initEmailSignup({ selector = ".port-email-widget" } = {}) {
    document.querySelectorAll(selector).forEach(initEmailSignupWidget);
}
